import { roundHalfUpCents } from '../money';

/**
 * The BOM's one ordering (piece 18; decision 0333): lines and named dividers
 * share a `position`, a line's group is the divider above it and is derived
 * rather than stored, and a row moves by being dragged or stepped up and
 * down. The rules here are pure; the API writes the positions and the Item
 * Table draws the sequence.
 */
export type BomRowKind = 'line' | 'divider';

export interface BomSequenceRow {
  kind: BomRowKind;
  id: string;
  position: number;
  /** A divider's name; a line carries none. */
  name?: string | null;
}

/** The rows in reading order: by position, a divider before a line at the same position, then by id so the order is total. */
export function sequenceRows<T extends BomSequenceRow>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => a.position - b.position || (a.kind === b.kind ? 0 : a.kind === 'divider' ? -1 : 1) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Each line's group: the name of the nearest divider above it, or null above the first divider. */
export function groupsOf(sequence: readonly BomSequenceRow[]): Map<string, string | null> {
  const out = new Map<string, string | null>();
  let current: string | null = null;
  for (const row of sequence) {
    if (row.kind === 'divider') current = row.name?.trim() || null;
    else out.set(row.id, current);
  }
  return out;
}

/** Positions rewritten 1..n in the order given, so a save leaves no gaps and no ties. */
export function normalisePositions<T extends { position: number }>(sequence: readonly T[]): T[] {
  return sequence.map((row, i) => ({ ...row, position: i + 1 }));
}

/** A row moved from one index to another; an index off the list leaves the list as it was. */
export function moveRow<T>(list: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return [...list];
  const out = [...list];
  const [row] = out.splice(from, 1);
  out.splice(to, 0, row as T);
  return out;
}

/** The position a fresh row takes: after every row the BOM already holds. */
export function nextPosition(rows: readonly { position: number }[]): number {
  return rows.reduce((max, row) => Math.max(max, row.position), 0) + 1;
}

/** The Deal value before and after a raise, in whole cents (T2192 item 12). */
export interface ValueRaise {
  fromCents: number;
  toCents: number;
}

/**
 * 🔨 The raise a BOM save names after Save: the floor above the value it stood
 * at (decision 0334). Since decision 0726 the save writes the value through the
 * best information, which can also lower it to the floor; a lowering is no raise
 * and this answers null for it. A value typed by hand still meets the floor rule
 * of decision 0048.
 */
export function valueRaise(dealValue: number, floor: number): ValueRaise | null {
  return floor > dealValue + 1e-9 ? { fromCents: roundHalfUpCents(dealValue * 100), toCents: roundHalfUpCents(floor * 100) } : null;
}
